import {
  RichTextEditor as BaseEditor,
  buildMergeFieldMap,
  getMergeFieldData,
  IMergeFieldKeys,
  RichTextEditorProps,
} from '@fluentsms/editors';
import { generateMergeKeyPlaceholder, getExpressionsAndDataSources, MergeField, parseField } from '@fluentsms/merge';
import { Box, Grid, makeStyles, Theme } from '@material-ui/core';
import cuid from 'cuid';
import { IntrospectionQuery } from 'graphql';
import { unescape } from 'lodash';
import React, { RefObject, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import RichTextEditorMergeFieldSection from './components/RichTextEditorMergeFieldSection';

export const useStyles = makeStyles((theme: Theme) => ({
  baseMergeField: {
    backgroundColor: theme.palette.secondary.light,
    fontWeight: theme.typography.fontWeightBold,
  },
  activeField: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  overriddenField: {
    backgroundColor: theme.palette.warning.dark,
    color: theme.palette.warning.contrastText,
  },
  inputRoot: {
    '&.MuiAutocomplete-inputRoot': {
      paddingRight: 0,
    },
  },
}));

export interface RichTextMergeFieldEditorProps extends Omit<RichTextEditorProps, 'onBlur'> {
  /**
   Optional merge field data that allows the editor to display existing merge field information.
   key: the merge field i.e. (order.header.statementType)
   value: the merge field data object
   */
  mergeFieldData?: Map<string, MergeField>;
  /**
   * Optional record of keys needed to run query
   */
  mergeFieldKeys?: IMergeFieldKeys;
  /**
   * Optional schema needed to determine if query is valid
   */
  schema?: IntrospectionQuery;
  /**
   Optional handler passed in that fires when:
   - click from inside the editor to outside of the editor
   - click from a merge field to outside of the editor
   Handler won't fire when:
   - click between the editor and a merge field
   - click between merge fields
   text is the most recent text content.
   html is the html representation of the text.
   mergeFields is the most recent merge fields data.
   */
  onBlur?: (text: string, html: string, mergeFields: Map<string, MergeField>) => void;
  onExit?: (obj: { text: string; html: string; mergeFields: Map<string, MergeField> }) => void;

  baseEditorRef?: RefObject<BaseEditor>;

  /**
   * Optional boolean that hide the merge panel inside the editor.
   */
  hideMergePanel?: boolean;
  fetchCrossDomainData?: boolean;
  // An ID passed all the way down to the editor itself.
  editorId?: string;
}

export function RichTextMergeFieldEditor(props: RichTextMergeFieldEditorProps): JSX.Element {
  const {
    editorId = '',
    textContent,
    mergeFieldData,
    mergeFieldKeys,
    schema,
    baseEditorRef = useRef<BaseEditor>(null),
    hideMergePanel = false,
    fetchCrossDomainData = true,
  } = props;

  const { onBlur, onExit, onChange, ...propsWithoutOnBlur } = props;
  // generate a random integer ID that will correspond to this editor to ensure that blur events are only ignored for this editor
  const id = cuid();
  const classes = useStyles();
  const { control, getValues } = useForm();

  const [mergeFields, setMergeFields] = useState<Map<string, MergeField>>(mergeFieldData || new Map());
  const [elements, setElements] = useState<Map<string, HTMLElement[]>>(new Map());
  const elementsRef = useRef<Map<string, HTMLElement[]>>(new Map());

  // Call function that handles onExit
  useEffect(() => {
    return () => {
      handleComponentClose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChange = (text: string, html: string, element: HTMLElement | undefined) => {
    if (!element) return;
    const elements = element?.getElementsByTagName('mergefield');
    const elementsMap = new Map();

    Array.from(elements).forEach((element: Element) => {
      const mergeFieldKey = element.getAttribute('mergeKey') as string;

      if (elementsMap.has(mergeFieldKey)) {
        const currElements = elementsMap.get(mergeFieldKey);
        currElements.push(element);
        elementsMap.set(mergeFieldKey, currElements);
      } else {
        elementsMap.set(mergeFieldKey, [element]);
      }
    });

    setElements(elementsMap);
    elementsRef.current = elementsMap;

    if (onChange) {
      onChange(text, html, element);
    }
  };

  // since the merge fields are wrapped before we map them in fetchMergeFields, we can assign the key tag here
  const wrapMergeFields = (text: string): string => {
    const baseElement = new DOMParser().parseFromString(text, 'text/html');
    const elements = baseElement.body.getElementsByTagName('mergefield');

    for (let i = 0; i < elements.length; i = i + 1) {
      const element = elements.item(i);
      // give each element a mergekey tag
      if (!element?.getAttribute('mergeKey')) {
        element?.setAttribute('mergeKey', element?.innerHTML);
      }
      if (element?.parentNode?.nodeName !== 'span' && element?.parentElement?.getAttribute('role') !== 'textbox') {
        const wrapper = document.createElement('span');
        wrapper.setAttribute('role', 'textbox');
        wrapper.setAttribute('contenteditable', 'false');
        wrapper.setAttribute('readonly', '');
        element?.parentNode?.insertBefore(wrapper, element);
        wrapper.appendChild(element as Element);
      }
    }
    return baseElement.body.outerHTML;
  };

  // Initialize merge fields, fetch merge fields, set elements
  const handleOnCreated = async (element: HTMLElement | undefined): Promise<void> => {
    if (!element) return;
    const elements = element.getElementsByTagName('mergefield');
    const elementsMap = new Map();
    let mergeFieldMap: Map<string, MergeField> = new Map(mergeFields);

    // If merge fields don't exist create the map
    if (!mergeFieldMap.size) {
      Array.from(elements).forEach((element: Element) => {
        const mergeFieldKey = element.getAttribute('mergeKey') as string;

        if (!mergeFieldMap.has(mergeFieldKey)) {
          mergeFieldMap.set(mergeFieldKey, parseField(mergeFieldKey, getExpressionsAndDataSources));
        }
      });
    }

    // Fetch cross domain data
    if (mergeFieldKeys && schema && fetchCrossDomainData) {
      const mergeFieldData = await getMergeFieldData(
        mergeFieldKeys,
        new Set(Array.from(mergeFieldMap.values()).map((mergeField: MergeField) => mergeField.dataSource)),
        schema,
      );

      mergeFieldMap = buildMergeFieldMap(mergeFieldMap, mergeFieldData);
    }

    // Set elements
    Array.from(elements).forEach((element: Element) => {
      // new merge key here.
      const mergeFieldKey = unescape(element.getAttribute('mergeKey') ?? '') as string;
      if (elementsMap.has(mergeFieldKey)) {
        const currElements = elementsMap.get(mergeFieldKey);
        currElements.push(element);
        element.innerHTML =
          mergeFieldMap.get(mergeFieldKey)?.fieldValue ||
          generateMergeKeyPlaceholder(mergeFieldMap.get(mergeFieldKey)?.fieldLabel || '');
        element.classList.add(classes.baseMergeField);
        if (mergeFieldMap.get(mergeFieldKey)?.isOverridden) element.classList.add(classes.overriddenField);
        elementsMap.set(mergeFieldKey, currElements);
      } else {
        element.innerHTML =
          mergeFieldMap.get(mergeFieldKey)?.fieldValue ||
          generateMergeKeyPlaceholder(mergeFieldMap.get(mergeFieldKey)?.fieldLabel || '');
        element.classList.add(classes.baseMergeField);
        if (mergeFieldMap.get(mergeFieldKey)?.isOverridden) element.classList.add(classes.overriddenField);
        elementsMap.set(mergeFieldKey, [element]);
      }
    });

    setMergeFields(mergeFieldMap);
    setElements(elementsMap);
    elementsRef.current = elementsMap;
  };

  const handleComponentClose = () => {
    const mergeFieldMap = new Map();
    const elements = elementsRef.current;
    const values = getValues().mergeFields;
    let index = 0;

    // Need to replace html and text values again
    elements.forEach((elemArray: HTMLElement[], key: string) => {
      const originalMergeField = mergeFields.get(key);
      const originalValue = originalMergeField?.originalFieldValue || '';

      elemArray.forEach((value: HTMLElement) => {
        if (originalValue) {
          value.innerHTML = originalValue;
          value.classList.remove(classes.activeField);
          value.classList.remove(classes.baseMergeField);
          value.classList.remove(classes.overriddenField);
        }
      });

      if (originalMergeField) {
        const value = values ? values[index] ?? '' : '';
        index++;

        originalMergeField.rawValue = value;
        mergeFieldMap.set(key, originalMergeField);
      }
    });
    const text = baseEditorRef.current?.getTextContent() || '';
    const html = baseEditorRef.current?.getTextContentHtml() || '';

    onExit && onExit({ text, html, mergeFields: mergeFieldMap });
  };

  // this handles onBlur on the entire component
  const handleComponentBlur = (event: React.FocusEvent) => {
    // Traverse parentElements to determine if the blur trigger Element is part of the RTE
    // Do not fire blur for elements related to the RTE
    // uses the ID for this editor to only ignore blur events for this editor
    const blurTrigger = event.relatedTarget as HTMLElement;
    if (
      blurTrigger &&
      blurTrigger.closest &&
      (blurTrigger.closest('.e-richtexteditor')?.getAttribute('id') === id ||
        blurTrigger.closest('.e-rte-dropdown-popup') ||
        blurTrigger.closest('.e-rte-quick-popup'))
    ) {
      return;
    }

    if (onBlur) {
      const currentTarget = event.currentTarget;

      // Check the newly focused element in the next tick of the event loop
      setTimeout(() => {
        // Check if the new activeElement is a child of the original container
        if (!currentTarget.contains(document.activeElement) && !document.getElementById('specialCharactersDialog')) {
          const text = baseEditorRef.current?.getTextContent() || '';
          const html = baseEditorRef.current?.getTextContentHtml() || '';
          // evoke onBlur handler from prop
          onBlur(text, html, mergeFields);
        }
      }, 0);
    }
  };

  return (
    <Grid
      id={id}
      data-testid="rich-text-editor-component-wrapper"
      container
      direction="column"
      onBlur={handleComponentBlur}
    >
      <Grid data-testid="rich-text-editor-wrapper" item xs={12}>
        <BaseEditor
          {...propsWithoutOnBlur} // do not pass the onBlur handler to the BaseEditor
          textContent={wrapMergeFields(textContent ?? '')}
          onCreated={handleOnCreated}
          onChange={handleOnChange}
          ref={baseEditorRef}
          editorId={editorId}
        />
      </Grid>
      {mergeFields.size > 0 && !hideMergePanel && (
        <Grid item data-testid="rich-text-editor-merge-field-container">
          <Box overflow="none">
            <RichTextEditorMergeFieldSection
              mergeFields={mergeFields}
              elements={elements}
              setMergeFields={setMergeFields}
              control={control}
            />
          </Box>
        </Grid>
      )}
    </Grid>
  );
}

export default RichTextMergeFieldEditor;
